/**
 * The store's frame around every page, in its default copy (the rule is in `shared.ts`): the links
 * each bar shows, the header search field's label, and the frame's own words.
 */
import type { StorefrontFeatureKey } from "../index.js";
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import { defaultLiveBreaksDirectoryCopy } from "./liveBreaks.js";

/** Where a link can appear: the header, the phone's tab bar, the account menu, the footer. */
export type StorefrontNavPlace = "header" | "tabs" | "accountMenu" | "footer";

/**
 * The storefront's links. `places` lists where one appears, and every bar reads it rather than
 * picking links itself, so a store's own arrangement is one override of it. `feature` hides a
 * link when the store has it off, and `label` reads the store's terms, so a store that sells
 * "packs" lists "Packs".
 */
// `as const` keeps `to` a literal, so the storefront's router type-checks each path.
export const STOREFRONT_NAV_LINKS = [
  {
    to: "/boxes",
    places: ["header", "tabs", "footer"],
    label: (t: StorefrontTerminology) => capitalizeTerm(t.box.plural),
    exact: false,
    feature: undefined,
  },
  {
    to: "/battles",
    places: ["header", "tabs", "footer"],
    label: (t: StorefrontTerminology) => capitalizeTerm(t.battle.plural),
    exact: false,
    feature: "battles",
  },
  {
    to: "/forge",
    places: ["header", "tabs", "footer"],
    label: (t: StorefrontTerminology) => capitalizeTerm(t.forge.singular),
    exact: false,
    feature: "forge",
  },
  // Last of the tab bar's links: a phone shows it there while Home, the others and Chat leave room.
  {
    to: "/live-breaks",
    places: ["header", "tabs", "footer"],
    label: (t: StorefrontTerminology) => defaultLiveBreaksDirectoryCopy(t).heading.title,
    exact: false,
    feature: "liveBreaks",
  },
  {
    to: "/inventory",
    places: ["accountMenu", "footer"],
    label: (t: StorefrontTerminology) => capitalizeTerm(t.inventory.singular),
    exact: false,
    feature: undefined,
  },
  {
    to: "/fairness",
    places: ["accountMenu", "footer"],
    label: () => "Fairness",
    exact: false,
    feature: undefined,
  },
  {
    to: "/account",
    places: ["accountMenu", "footer"],
    label: () => "Account",
    exact: false,
    feature: undefined,
  },
] as const satisfies readonly {
  to: string;
  places: readonly StorefrontNavPlace[];
  label: (terms: StorefrontTerminology) => string;
  exact: boolean;
  feature?: StorefrontFeatureKey;
}[];

/** One of the storefront's link targets. */
export type StorefrontNavPath = (typeof STOREFRONT_NAV_LINKS)[number]["to"];

/** A link the store shows, in its words. */
export interface StorefrontNavLink {
  to: StorefrontNavPath;
  places: readonly StorefrontNavPlace[];
  label: string;
  exact: boolean;
}

/** The links a store shows: those whose feature it has on, labeled in its terms. */
export function visibleStorefrontNavLinks(
  hasFeature: (key: StorefrontFeatureKey) => boolean,
  terms: StorefrontTerminology,
): StorefrontNavLink[] {
  return STOREFRONT_NAV_LINKS.filter((link) => !link.feature || hasFeature(link.feature)).map(
    (link) => ({ to: link.to, places: link.places, label: link.label(terms), exact: link.exact }),
  );
}

/** The header search field's label. */
export function storefrontSearchLabel(terms: StorefrontTerminology): string {
  return `Search ${terms.box.plural}`;
}

/**
 * The frame's own words: the phone tab bar's Home and Chat tabs, a docked chat's tab, the
 * header's Deposit and Log in, and the footer's Store and Legal columns' titles. The Legal column's links are the policies'
 * own titles (`STOREFRONT_POLICY_TITLES`), platform words.
 */
export const STOREFRONT_FRAME_COPY = {
  home: "Home",
  chat: "Chat",
  showChat: "Show chat",
  hideChat: "Hide chat",
  deposit: "Deposit",
  logIn: "Log in",
  storeColumn: "Store",
  legalColumn: "Legal",
} as const;

/**
 * The footer's pack tear (`layout.footer: "tear"`): over the revealed card (a sample from the
 * store's boxes, never a pull, so never "you pulled" or "you won"), on the strip for a pointer and
 * for touch, the strip's accessible name, Reset, and the way to the catalog.
 */
export function defaultFooterTearCopy(terms: StorefrontTerminology): {
  caption: string;
  hint: string;
  hintTouch: string;
  strip: string;
  reset: string;
  action: string;
} {
  return {
    caption: `Could be in your next ${terms.box.singular}`,
    hint: "Pull to rip",
    hintTouch: "Swipe to rip",
    strip: "Rip the strip open",
    reset: "Reset",
    action: `Rip ${terms.box.plural}`,
  };
}
